import { useCallback, useEffect, useRef } from 'react';
import {
  getAllChannelsAndThreads,
  getCurrentChannelId,
  getUserId,
  getWorkspaceId,
  incrementUnreadMessageCount,
  dispatch,
  store,
  upsertChannel,
  upsertMessage,
  useAppSelector,
  ChannelTypeEnum,
  useLazyGetCentrifugoSubscriptionQuery,
  useLazyGetChannelDetailsQuery,
  getIsAuthenticated,
  getAllMessages,
  getChannelMessages,
} from '@zonofi/seller-rtk-store';
import { Centrifuge, PublicationContext, SubscribedContext } from 'centrifuge';
import {
  MessageEnvelope,
  MessagePayload,
  MessageTagData,
  normalizeTimeTokenTo17Digits,
} from '@zonofi/common';
import { isString } from 'lodash';

interface PublishedActions {
  channel: string;
  messageTimetoken: string;
  messageId: string;
  actions: {
    type: 'tags' | 'status' | 'reaction';
    value: string; // keep as raw string (no parsing)
  }[];
}

function formatIncomingMessageText(text: unknown): string {
  if (text === undefined || text === null || text === false) return '';
  return isString(text) ? text : String(text);
}

const centrifugoLog = (
  level: 'log' | 'warn' | 'error' | 'info',
  ...args: unknown[]
) => {
  const timestamp = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
  console[level](`[Centrifugo] ${timestamp}`, ...args);
};

export const useSetupCentrifugo = () => {
  const [fetchConfig] = useLazyGetCentrifugoSubscriptionQuery();
  const [fetchChannelDetails] = useLazyGetChannelDetailsQuery();
  const userId = useAppSelector(getUserId);
  const currentChannel = useAppSelector(getCurrentChannelId);
  const workspaceId = useAppSelector(getWorkspaceId);
  const isAuthenticated = useAppSelector(getIsAuthenticated);

  const currentChannelRef = useRef(currentChannel);
  const centrifugeRef = useRef<Centrifuge | null>(null);

  // Keep refs in sync on every render
  currentChannelRef.current = currentChannel;

  const handleMessage = useCallback(
    async (message: MessagePayload) => {
      try {
        const {
          text = '',
          type,
          sellerDetails,
          channelMetadata,
          cId: channel,
          cAt,
          sellerWorkspaceId,
        } = message || {};

        const timetoken = normalizeTimeTokenTo17Digits(String(cAt));
        if (!channel) {
          centrifugoLog('warn', 'message missing channel, skipping', message);
          return;
        }

        const msgWorkspaceId = sellerDetails?.id || sellerWorkspaceId;
        if (msgWorkspaceId !== workspaceId) {
          centrifugoLog('info', 'message ignored (workspace mismatch)', {
            msgWorkspaceId,
            expectedWorkspaceId: workspaceId,
          });
          return;
        }

        const messageText = formatIncomingMessageText(message?.text);

        dispatch(
          upsertMessage({
            message: { ...message, text: messageText },
            channel,
            timetoken,
          })
        );

        if (channel !== currentChannelRef.current) {
          dispatch(incrementUnreadMessageCount(channel));
        }

        const allChannels = getAllChannelsAndThreads(store.getState());
        let chnlDetails = allChannels?.[channel];

        if (!chnlDetails && workspaceId) {
          try {
            chnlDetails = await fetchChannelDetails({
              channelId: channel,
              workspaceId: workspaceId,
              sellerWorkspaceId: sellerDetails?.id || workspaceId,
            }).unwrap();
            dispatch(upsertChannel({ [channel]: chnlDetails }));
          } catch (error) {
            centrifugoLog('warn', 'failed to fetch missing channel details', {
              channel,
              error,
            });
          }
        }

        dispatch(
          upsertChannel({
            [channel]: {
              ...chnlDetails,
              ...channelMetadata,
              latestMessage: channelMetadata?.latestMessage || text || type,
              latestMessageTimeToken:
                channelMetadata?.latestMessageTimeToken || timetoken,
              ...(chnlDetails?.type === ChannelTypeEnum.ISSUETHREAD
                ? {
                    totalMessageCount:
                      (chnlDetails?.totalMessageCount || 0) + 1,
                  }
                : {}),
            },
          })
        );
      } catch (error) {
        centrifugoLog('error', 'error handling message', error);
      }
    },
    [workspaceId]
  );

  const handleActions = useCallback(
    async (data: PublishedActions) => {
      const { channel, messageId, actions } = data;
      if (!channel || !messageId) return;
      const channelMessages = getChannelMessages(channel)(store.getState());
      let msgEnvelope: MessageEnvelope | undefined = channelMessages?.find(
        (x) => x.message._id === messageId
      );
      if (!msgEnvelope) return;
      let updatedMessage = { ...msgEnvelope.message };
      actions.forEach(({ type, value }) => {
        switch (type) {
          case 'tags': {
            try {
              const parsed = JSON.parse(value) as MessageTagData;
              updatedMessage = { ...updatedMessage, tags: [parsed] };
            } catch (e) {
              centrifugoLog('error', 'error handling tags', e);
            }
            break;
          }
          case 'status': {
            const currentStats = updatedMessage.stats || {};
            let status = value;
            let description = '';
            if (value.toLowerCase().includes('error')) {
              status = 'error';
              description = value.split('|')?.[1];
            }
            const updatedStats = {
              ...currentStats,
              [status]: { dateTime: `${Date.now()}0000`, description },
            };
            updatedMessage = {
              ...updatedMessage,
              stats: updatedStats,
            };
            break;
          }
          case 'reaction': {
            updatedMessage = {
              ...updatedMessage,
              reaction: { emoji: value, timetoken: `${Date.now()}0000` },
            };
            break;
          }
          default:
            break;
        }
      });
      dispatch(
        upsertMessage({
          ...msgEnvelope,
          message: updatedMessage,
        })
      );
    },
    [workspaceId]
  );

  useEffect(() => {
    if (!userId || !workspaceId) return () => {};

    if (!isAuthenticated) {
      if (centrifugeRef.current) {
        centrifugoLog('log', 'user unauthenticated, disconnecting');
        centrifugeRef.current.disconnect();
        centrifugeRef.current = null;
      }
      return () => {};
    }

    let centrifuge: Centrifuge | null = null;
    let cancelled = false;
    let reconnectAttempt = 0;

    // Always bypasses RTK cache — used for token refresh and reconnect
    const fetchFreshConfig = () =>
      fetchConfig(
        { sellerWorkspaceId: workspaceId },
        false // preferCacheValue: false → forces real network request
      ).unwrap();

    const init = async () => {
      try {
        // Initial load can use cache for websocketUrl + channel name
        const config = await fetchConfig(
          { sellerWorkspaceId: workspaceId },
          true
        ).unwrap();

        if (cancelled) return null;

        centrifuge = new Centrifuge(config.websocketUrl, {
          // (1) Centrifugo calls this automatically on connect + token expiry
          getToken: async () => {
            const fresh = await fetchFreshConfig();
            return fresh.token;
          },
          debug: true,
        });

        //  (2) Connection lifecycle listeners
        centrifuge.on('connecting', (ctx) => {
          centrifugoLog('log', 'connecting', ctx);
        });

        centrifuge.on('connected', (ctx) => {
          centrifugoLog('log', 'connected', ctx);
          reconnectAttempt = 0; // Reset backoff on successful connection
        });

        centrifuge.on('disconnected', (ctx) => {
          centrifugoLog('warn', 'disconnected', ctx);

          // code 0 = intentional disconnect() call, don't reconnect
          // code 1 = transport stopped unexpectedly, reconnect
          if (ctx.code !== 0 && !cancelled) {
            const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000); // caps at 30s
            centrifugoLog(
              'log',
              `reconnecting in ${delay}ms (attempt ${reconnectAttempt + 1})`
            );
            reconnectAttempt++;
            setTimeout(() => {
              if (!cancelled) centrifuge?.connect();
            }, delay);
          }
        });

        const subscription = centrifuge.newSubscription(
          config.subscriptions[0].channel
        );

        //  (3) Incoming message listener and handler
        subscription.on('publication', (ctx: PublicationContext) => {
          try {
            const data = ctx.data;

            if (data.isZoey) {
              centrifugoLog('log', 'zoey', data);
              return;
            }

            if (Array.isArray(data?.actions) && data.actions.length > 0) {
              centrifugoLog('log', 'action', data);
              handleActions(data as PublishedActions);
              return;
            }

            const message = data as MessagePayload;
            centrifugoLog('log', 'message', message);
            handleMessage(message);
          } catch (error) {
            centrifugoLog('error', 'error handling publication', {
              data: ctx.data,
              error,
            });
          }
        });

        subscription.on('subscribed', (ctx: SubscribedContext) => {
          centrifugoLog('log', 'subscribed to inbox:', ctx);
        });

        //  (4) Subscription error handling
        subscription.on('error', (ctx) => {
          centrifugoLog('error', 'subscription error:', ctx);
        });

        subscription.subscribe();
        centrifuge.connect();

        centrifugeRef.current = centrifuge;
      } catch (error) {
        centrifugoLog('error', 'init failed', error);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (centrifugeRef.current) {
        centrifugoLog('log', 'cleanup: disconnecting');
        centrifugeRef.current.disconnect();
        centrifugeRef.current = null;
      }
    };
  }, [userId, workspaceId, isAuthenticated]);
};
